/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input, Table, Typography, Space, Tooltip, Button, Badge, Row, Col, Tag, Spin, Select } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import Highlighter from 'react-highlight-words';
import axios from 'axios';
import moment from 'moment';
import Utils from '../../misc/Utils';
import Enums from '../../misc/Enums';

const { Text } = Typography;
const { Option } = Select;

const ALL_BATCHES = '';

//-- 1TVET keeps this in Utils.hasOldApplicantAccess, but CC's shared Utils does not expose it and
//-- adding it there would break the "CC shared files stay byte-identical" rule. Inlined instead.
function hasOldApplicantAccess() {
   const user_role = Utils.getCurrentUserInfo(Enums.UserInfo.role);

   //-- Accounts with department = -100 are tagged as App-Admin during login
   if (user_role === 'Registrar' || user_role === 'App-Admin')
      return true;

   try {
      const other_data = JSON.parse(Utils.getCurrentUserInfo(Enums.UserInfo.otherdata) ?? '{}');
      return Number(other_data.Department) === -100;
   } catch (error) {
      return false;
   }
}

export function ApplicantListByBatch() {
   const navigate = useNavigate();
   const [searchText, setSearchText] = useState("");
   const [searchedColumn, setSearchedColumn] = useState("");

   const [legendCount, setLegendCount] = useState({
      pending: 0,
      accepted: 0,
      not_accepted: 0,
      ready_for_exam: 0,
      passed: 0,
      failed: 0,
      approved: 0,
      not_approved: 0,
      for_interview: 0,
      reconsidered: 0,
      rejected: 0
   });

   const [batchList, setBatchList] = useState([]);
   const [selectedBatch, setSelectedBatch] = useState(null);
   const [applicantList, setApplicantList] = useState([]);
   const [applicantListFiltered, setApplicantListFiltered] = useState([]);
   const [listLoading, setListLoading] = useState(false);

   useEffect(() => {
      Utils.verifyJwt();

      //-- Only Registrar accounts and accounts with department = -100 (App-Admin) can access this page
      if (!hasOldApplicantAccess())
         navigate('/home');

      //-- No records are loaded on page load, applicants are fetched once a batch is selected
      getBatches();
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);

   const handleSearch = (selectedKeys, confirm, dataIndex) => {
      confirm();
      setSearchText(selectedKeys[0]);
      setSearchedColumn(dataIndex);
   };

   const handleReset = clearFilters => {
      clearFilters();
      setSearchText("");
   };

   const getColumnSearchProps = dataIndex => ({
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
         <div style={{ padding: 8 }}>
            <Input
               ref={node => {
                  //this.searchInput(node);
               }}
               placeholder={`Search ${dataIndex}`}
               value={selectedKeys[0]}
               onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
               onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
               style={{ width: 188, marginBottom: 8, display: 'block' }}
            />
            <Space>
               <Button
                  type="primary"
                  onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
                  icon={<SearchOutlined />}
                  size="small"
                  style={{ width: 90 }}>
                  Search
               </Button>
               <Button onClick={() => handleReset(clearFilters)} size="small" style={{ width: 90 }}>
                  Reset
               </Button>
               <Button
                  type="link"
                  size="small"
                  onClick={() => {
                     confirm({ closeDropdown: false });
                     setSearchText(selectedKeys[0]);
                     setSearchedColumn(dataIndex);
                  }}
               >
                  Filter
               </Button>
            </Space>
         </div>
      ),
      filterIcon: filtered => <SearchOutlined style={{ color: filtered ? '#033D89' : undefined }} />,
      onFilter: (value, record) =>
         record[dataIndex]
            ? record[dataIndex].toString().toLowerCase().includes(value.toLowerCase())
            : '',
      render: text =>
         searchedColumn === dataIndex ? (
            <Highlighter
               highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
               searchWords={[searchText]}
               autoEscape
               textToHighlight={text ? text.toString() : ''}
            />
         ) : (
            text
         ),
   });

   // Helper function to create name column config
   const createNameColumn = (key, title, width) => ({
      title,
      key,
      sorter: (a, b) => a[key].localeCompare(b[key]),
      ...getColumnSearchProps(key),
      ellipsis: { showTitle: key !== 'last_name' },
      render: obj => (
         <Tooltip placement="topLeft" title={obj[key]}>
            <Text>
               <span style={{ textTransform: 'capitalize' }}>{obj[key]}</span>
            </Text>
         </Tooltip>
      ),
      width
   });

   const columns = [
      { title: 'Id', key: 'id', dataIndex: 'id', hidden: true },
      createNameColumn('last_name', 'Last Name', '14%'),
      createNameColumn('first_name', 'First Name', '14%'),
      createNameColumn('middle_name', 'Middle Name', '14%'),
      {
         title: 'Gender',
         key: 'gender',
         sorter: (a, b) => (a.gender ?? '').localeCompare(b.gender ?? ''),
         ...getColumnSearchProps('gender'),
         ellipsis: { showTitle: true },
         render: obj => (
            <Text>
               {obj.gender && <span style={{ textTransform: 'capitalize' }}>{obj.gender}</span>}
            </Text>
         ),
         width: '8%'
      },
      {
         title: 'Date Applied',
         key: 'date_applied',
         sorter: (a, b) => (a.date_applied ?? '').localeCompare(b.date_applied ?? ''),
         ...getColumnSearchProps('date_applied'),
         ellipsis: { showTitle: true },
         render: obj => (
            <Text>{moment(obj.date_applied).format('MMM. DD, YYYY hh:mm:ss A')}</Text>
         ),
         width: '15%'
      },
      {
         title: 'Application Status',
         key: 'application_status',
         ...getColumnSearchProps('application_status'),
         ellipsis: { showTitle: true },
         render: obj => (
            <>
               {(!obj.exam_status && !['approved', 'for_interview', 'ready_for_exam'].includes(obj.application_status) && obj.is_pop_valid) && RenderTag('paid')}
               {(obj.exam_status && ['approved', 'for_interview', 'on_hold'].includes(obj.application_status)) && RenderTag(obj.exam_status)}
               {RenderTag(obj.application_status)}
            </>
         ),
         width: '22%'
      },
      {
         title: 'Batch',
         key: 'batch',
         sorter: (a, b) => (a.batch ?? '').localeCompare(b.batch ?? ''),
         ...getColumnSearchProps('batch'),
         ellipsis: { showTitle: true },
         render: obj => (
            <Text>{obj.batch ? 'Batch ' + obj.batch : ''}</Text>
         ),
         width: '8%'
      },
      {
         title: 'Action',
         key: 'action',
         render: ({ id, application_status: status }) => (
            <Space size="small">
               <Button type="primary" size="small" shape="round" onClick={() => handleViewApplicant(id, status)}>View</Button>
            </Space>
         ),
         width: '8%'
      }
   ].filter(item => !item.hidden);

   const getBatches = async () => {
      try {
         const { data } = await axios.get(`${process.env.REACT_APP_API_CPH}/getapplicantbatches`);
         if (data) {
            setBatchList(data);
         }
      } catch (error) {
         console.error('Failed to fetch batches:', error.message);
      }
   };

   const getApplicants = async (batch) => {
      setListLoading(true);
      try {
         const { data } = await axios.get(`${process.env.REACT_APP_API_CPH}/getonlineapplicantsbybatch?batch=${batch ?? ''}`);
         if (data) {
            const legendCount = data.reduce((counts, { application_status }) => {
               const status = application_status === 'on_hold' ? 'for_interview' : application_status;
               counts[status] = (counts[status] || 0) + 1;
               return counts;
            }, {
               pending: 0,
               accepted: 0,
               rejected: 0,
               ready_for_exam: 0,
               passed: 0,
               failed: 0,
               approved: 0,
               not_approved: 0,
               for_interview: 0,
               reconsidered: 0,
            });

            setLegendCount(legendCount);
            setApplicantList(data);
            setApplicantListFiltered(data);
         }
      } catch (error) {
         console.error('Failed to fetch applicants:', error.message);
      } finally {
         setListLoading(false);
      }
   };

   const handleBatchChange = (value) => {
      const batch = value ?? ALL_BATCHES;

      //-- Clear the previously displayed records before loading the selected batch
      setApplicantList([]);
      setApplicantListFiltered([]);

      setSelectedBatch(batch);
      getApplicants(batch);
   };

   const handleFilterListByStatus = (status) => {
      var filteredList = applicantList.filter(student => student.application_status === status);
      setApplicantListFiltered(filteredList);
   };

   //-- Users can only view the applicant information in this page
   const handleViewApplicant = (id, status) => {
      navigate(`/reviewapplication?id=${id}`, {
         state: { status, mode: 'view', from: '/applicantsbybatch' }
      });
   };


   const RenderTag = (status) => {
      var color = '';

      switch (status) {
         case 'pending':
            color = 'gold';
            break;
         case 'accepted':
            color = 'green'
            break;
         case 'rejected':
            color = 'red';
            break;
         case 'ready_for_exam':
            color = 'pink';
            break;
         case 'exam_passed':
            color = 'green';
            break;
         case 'exam_failed':
            color = 'red';
            break;
         case 'approved':
            color = 'cyan';
            break;
         case 'not_approved':
            color = 'red';
            break;
         case 'on_hold':
         case 'for_interview':
            color = 'blue';
            break;
         case 'reconsidered':
            color = 'purple';
            break;
         case 'paid':
            color = 'cyan';
            break;
         case 'enrolled':
            color = 'green';
            break;
         default:
      }

      return <Tag color={color}>
         {
            status === 'on_hold'
               ?
               'INTERVIEW ' + status.replace(/_/g, ' ').toUpperCase()
               :
               status.replace(/_/g, ' ').toUpperCase()
         }
      </Tag>
   }



   const RenderStatusCounts = () => {
      return (
         <div style={{ overflow: 'auto' }}>
            <Row style={{ paddingTop: "20px", minWidth: '600px', marginBottom: '20px' }} >
               <Col span={24} style={{ display: 'flex', justifyContent: 'center' }}>
                  <Space size={'middle'}>
                     <Badge count={legendCount.pending} style={{ backgroundColor: 'gold' }} overflowCount={999}>
                        <Tag
                           color={"gold"}
                           style={{ cursor: legendCount.pending > 0 ? 'pointer' : 'default', padding: '7px' }}
                           onClick={() => handleFilterListByStatus('pending')}
                        >
                           PENDING
                        </Tag>
                     </Badge>
                     <Badge count={legendCount.accepted} style={{ backgroundColor: 'green' }} overflowCount={999}>
                        <Tag
                           color={"green"}
                           style={{ cursor: legendCount.accepted > 0 ? 'pointer' : 'default', padding: '7px' }}
                           onClick={() => handleFilterListByStatus('accepted')}
                        >
                           ACCEPTED
                        </Tag>
                     </Badge>
                     <Badge count={legendCount.rejected} style={{ backgroundColor: 'red' }} overflowCount={999}>
                        <Tag
                           color={"red"}
                           style={{ cursor: legendCount.rejected > 0 ? 'pointer' : 'default', padding: '7px' }}
                           onClick={() => handleFilterListByStatus('rejected')}
                        >
                           REJECTED
                        </Tag>
                     </Badge>
                     <Badge count={legendCount.ready_for_exam} style={{ backgroundColor: 'pink' }} overflowCount={999}>
                        <Tag
                           color={"pink"}
                           style={{ cursor: legendCount.ready_for_exam > 0 ? 'pointer' : 'default', padding: '7px' }}
                           onClick={() => handleFilterListByStatus('ready_for_exam')}
                        >
                           READY FOR EXAM
                        </Tag>
                     </Badge>
                     <Badge count={legendCount.passed} style={{ backgroundColor: 'green' }} overflowCount={999}>
                        <Tag
                           color={"green"}
                           style={{ cursor: legendCount.passed > 0 ? 'pointer' : 'default', padding: '7px' }}
                           onClick={() => handleFilterListByStatus('exam_passed')}
                        >
                           EXAM PASSED
                        </Tag>
                     </Badge>
                     <Badge count={legendCount.failed} style={{ backgroundColor: 'red' }} overflowCount={999}>
                        <Tag
                           color={"red"}
                           style={{ cursor: legendCount.failed > 0 ? 'pointer' : 'default', padding: '7px' }}
                           onClick={() => handleFilterListByStatus('exam_failed')}
                        >
                           EXAM FAILED
                        </Tag>
                     </Badge>
                     <Badge count={legendCount.reconsidered} style={{ backgroundColor: 'purple' }} overflowCount={999}>
                        <Tag
                           color={"purple"}
                           style={{ cursor: legendCount.reconsidered > 0 ? 'pointer' : 'default', padding: '7px' }}
                           onClick={() => handleFilterListByStatus('reconsidered')}
                        >
                           RECONSIDERED
                        </Tag>
                     </Badge>
                     <Badge count={legendCount.for_interview} style={{ backgroundColor: 'blue' }} overflowCount={999}>
                        <Tag
                           color={"blue"}
                           style={{ cursor: legendCount.for_interview > 0 ? 'pointer' : 'default', padding: '7px' }}
                           onClick={() => handleFilterListByStatus('for_interview')}
                        >
                           FOR INTERVIEW
                        </Tag>
                     </Badge>
                     <Badge count={legendCount.approved} style={{ backgroundColor: 'cyan' }} overflowCount={999}>
                        <Tag
                           color={"cyan"}
                           style={{ cursor: legendCount.approved > 0 ? 'pointer' : 'default', padding: '7px' }}
                           onClick={() => handleFilterListByStatus('approved')}
                        >
                           APPROVED
                        </Tag>
                     </Badge>
                  </Space>
               </Col>
            </Row>
         </div>
      );
   }

   return (
      <>
         <div className="dashboard-area-all">
            <div className="dashboard-item">
               <Spin size='large' spinning={listLoading} tip="Processing request...Please wait.">
                  <div className="dashboard-item-inner">
                     <ul>
                        <li className="active">Old Student Applicants</li>
                     </ul>
                  </div>

                  <div className="dashboard-item-inner3">
                     <Row gutter={[12, 12]} style={{ paddingTop: '15px' }}>
                        <Col xs={24} sm={24} md={8} lg={8}>
                           <Select
                              showArrow
                              size='middle'
                              style={{ width: '100%' }}
                              placeholder="Select Batch"
                              value={selectedBatch}
                              onChange={(val) => handleBatchChange(val)}
                           >
                              <Option value={ALL_BATCHES}>All Batches</Option>
                              {batchList.map(({ value, name }) => {
                                 return <Option key={value} value={value}>{name}</Option>
                              })}
                           </Select>
                        </Col>
                        <Col xs={24} sm={24} md={16} lg={16} style={{ display: 'flex', justifyContent: 'right' }}>
                           <Space>
                              <Button className='button-shadow' type='primary' shape='round' disabled={selectedBatch === null} onClick={() => getApplicants(selectedBatch ?? ALL_BATCHES)}>Refresh</Button>
                           </Space>
                        </Col>
                     </Row>

                     {selectedBatch !== null && RenderStatusCounts()}

                     <Table
                        loading={listLoading}
                        size='small'
                        rowKey={'id'}
                        columns={columns}
                        dataSource={applicantListFiltered}
                        pagination={false}
                        locale={{ emptyText: 'Select a batch to view the old student applicants.' }}
                        scroll={{
                           x: '1000px',
                           y: 'calc(100vh - 24rem)',
                        }}
                        footer={() => 'Total Records: ' + applicantListFiltered.length}
                     />
                  </div>
               </Spin>
            </div>
         </div>
      </>
   );
}

